// src/pages/DriverPortal.js
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import './DriverPortal.css';

export default function DriverPortal() {
  const [searchParams] = useSearchParams();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [driverCode, setDriverCode] = useState('');
  const [driverName, setDriverName] = useState('');
  const [deliveries, setDeliveries] = useState([]);
  const [activeDelivery, setActiveDelivery] = useState(null);
  const [locationPermission, setLocationPermission] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [watchId, setWatchId] = useState(null);

  useEffect(() => {
    // Check if already logged in
    const savedDriver = localStorage.getItem('driver_authenticated');
    const savedName = localStorage.getItem('driver_name');
    if (savedDriver === 'true' && savedName) {
      setIsAuthenticated(true);
      setDriverName(savedName);
      loadDeliveries();
    }

    // Check for direct order link
    const orderParam = searchParams.get('order');
    if (orderParam && savedDriver === 'true') {
      // Auto-load specific order
      loadSpecificOrder(orderParam);
    }
  }, [searchParams]);

  const handleLogin = () => {
    // Simple code verification (in production, use real auth)
    if (driverCode === 'DRIVER2024' && driverName.trim()) {
      setIsAuthenticated(true);
      localStorage.setItem('driver_authenticated', 'true');
      localStorage.setItem('driver_name', driverName);
      loadDeliveries();
    } else {
      alert('Invalid driver code. Use: DRIVER2024');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setDriverName('');
    localStorage.removeItem('driver_authenticated');
    localStorage.removeItem('driver_name');
    stopLocationTracking();
  };

  const loadDeliveries = async () => {
    try {
      // TODO: Replace with real API call
      const response = await fetch('/api/driver/deliveries', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('driver_token') || 'DRIVER2024'}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setDeliveries(data.deliveries || []);
      } else {
        // Demo data for testing
        setDeliveries([
          {
            id: 'ORD-' + Date.now(),
            orderNumber: 'ORD-' + Date.now(),
            status: 'ready',
            customer: 'John Doe',
            address: '123 Main St, Boston, MA 02101',
            phone: '(555) 123-4567',
            deliveryTime: '17:00-19:00',
            items: [
              { name: 'Lobster Roll', quantity: 2 },
              { name: 'Boston Clam Chowder', quantity: 1 }
            ],
            total: 6600,
            chef: 'Chef Stu Rader',
            chefPhone: '(555) 987-6543'
          }
        ]);
      }
    } catch (error) {
      console.error('Error loading deliveries:', error);
      // Set demo data on error
      setDeliveries([]);
    }
  };

  const loadSpecificOrder = async (orderNumber) => {
    // Load specific order and set as active
    const order = deliveries.find(d => d.orderNumber === orderNumber);
    if (order) {
      setActiveDelivery(order);
    }
  };

  const requestLocationPermission = async () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return false;
    }

    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0
        });
      });

      console.log('📍 Location permission granted:', position.coords);
      setLocationPermission(true);
      setCurrentLocation({
        lat: position.coords.latitude,
        lng: position.coords.longitude
      });
      return true;
    } catch (error) {
      console.error('Location permission denied:', error);
      alert('Location access is required to start delivery. Please enable location services.');
      return false;
    }
  };

  const startLocationTracking = (orderId) => {
    if (!navigator.geolocation) return;

    const id = navigator.geolocation.watchPosition(
      (position) => {
        const location = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: Date.now()
        };

        console.log('📍 Location update:', location);
        setCurrentLocation(location);

        // Send location to server
        updateDriverLocation(orderId, location);
      },
      (error) => {
        console.error('Location error:', error);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );

    setWatchId(id);
    console.log('✅ Location tracking started, watch ID:', id);
  };

  const stopLocationTracking = () => {
    if (watchId) {
      navigator.geolocation.clearWatch(watchId);
      setWatchId(null);
      console.log('⏹️ Location tracking stopped');
    }
  };

  const updateDriverLocation = async (orderId, location) => {
    try {
      await fetch('/api/driver/location', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('driver_token') || 'DRIVER2024'}`
        },
        body: JSON.stringify({
          orderId,
          driverName,
          ...location
        })
      });
      console.log('📡 Location sent to server');
    } catch (error) {
      console.error('Error updating location:', error);
    }
  };

  const handleStartDelivery = async (delivery) => {
    // Request location permission first
    const hasPermission = await requestLocationPermission();
    if (!hasPermission) return;

    // Update order status to "out for delivery"
    try {
      await fetch('/api/driver/status', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          orderId: delivery.id,
          status: 'out_for_delivery',
          driverName
        })
      });

      setActiveDelivery(delivery);
      startLocationTracking(delivery.id);
      
      // Update local state
      setDeliveries(deliveries.map(d => 
        d.id === delivery.id ? { ...d, status: 'out_for_delivery' } : d
      ));

      alert('✅ Delivery started! Your location is now being shared.');
    } catch (error) {
      console.error('Error starting delivery:', error);
      alert('Error starting delivery. Please try again.');
    }
  };

  const formatPrice = (cents) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  const handleMarkDelivered = async (delivery) => {
    if (!window.confirm('Mark this order as delivered?')) return;

    try {
      await fetch('/api/driver/status', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          orderId: delivery.id,
          status: 'delivered',
          driverName,
          deliveredAt: new Date().toISOString()
        })
      });

      stopLocationTracking();
      setActiveDelivery(null);
      
      // Remove from deliveries list
      setDeliveries(deliveries.filter(d => d.id !== delivery.id));

      alert('✅ Order marked as delivered!');
    } catch (error) {
      console.error('Error marking delivered:', error);
      alert('Error updating status. Please try again.');
    }
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="driver-portal">
        <div className="driver-login">
          <div className="login-header">
            <h1>🚗 Driver Portal</h1>
            <p>Chefs2Table Delivery</p>
          </div>
          <div className="login-form">
            <div className="form-group">
              <label htmlFor="driverName">Your Name</label>
              <input
                id="driverName"
                type="text"
                value={driverName}
                onChange={(e) => setDriverName(e.target.value)}
                placeholder="Enter your name"
                onKeyPress={(e) => e.key === 'Enter' && driverCode && handleLogin()}
              />
            </div>
            <div className="form-group">
              <label htmlFor="driverCode">Driver Code</label>
              <input
                id="driverCode"
                type="password"
                value={driverCode}
                onChange={(e) => setDriverCode(e.target.value)}
                placeholder="Enter driver code"
                onKeyPress={(e) => e.key === 'Enter' && driverName && handleLogin()}
              />
            </div>
            <button className="login-btn" onClick={handleLogin}>
              Sign In
            </button>
            <div className="login-help">
              <small>Demo Code: DRIVER2024</small>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main Driver Interface
  return (
    <div className="driver-portal">
      <div className="driver-header">
        <div>
          <h1>🚗 Driver Portal</h1>
          <p className="driver-welcome">Welcome, {driverName}!</p>
        </div>
        <button className="logout-btn" onClick={handleLogout}>Logout</button>
      </div>

      {/* Location Status */}
      <div className={`location-status ${locationPermission ? 'enabled' : 'disabled'}`}>
        {locationPermission ? (
          <>
            <span className="status-icon">📍</span>
            <span>Location Sharing: Active</span>
            {currentLocation && (
              <span className="coords">({currentLocation.lat.toFixed(6)}, {currentLocation.lng.toFixed(6)})</span>
            )}
          </>
        ) : (
          <>
            <span className="status-icon">⚠️</span>
            <span>Location Sharing: Inactive (will activate when you start a delivery)</span>
          </>
        )}
      </div>

      {/* Active Delivery */}
      {activeDelivery && (
        <div className="active-delivery">
          <h2>🚗 Active Delivery</h2>
          <div className="delivery-card active">
            <div className="delivery-header">
              <span className="order-num">{activeDelivery.orderNumber}</span>
              <span className="status-badge out-for-delivery">Out for Delivery</span>
            </div>
            <div className="delivery-info">
              <p><strong>Customer:</strong> {activeDelivery.customer}</p>
              <p><strong>Phone:</strong> {activeDelivery.phone}</p>
              <p><strong>Address:</strong> {activeDelivery.address}</p>
              <p><strong>Window:</strong> {activeDelivery.deliveryTime}</p>
            </div>
            <div className="delivery-items">
              <strong>Items:</strong>
              {activeDelivery.items.map((item, idx) => (
                <div key={idx}>{item.quantity}x {item.name}</div>
              ))}
            </div>
            <div className="delivery-actions">
              <a href={`tel:${activeDelivery.phone}`} className="btn-call">📞 Call Customer</a>
              <a href={`https://maps.google.com/?q=${encodeURIComponent(activeDelivery.address)}`} 
                 target="_blank" 
                 rel="noopener noreferrer" 
                 className="btn-navigate">
                🗺️ Navigate
              </a>
              <button 
                className="btn-delivered"
                onClick={() => handleMarkDelivered(activeDelivery)}
              >
                ✅ Mark Delivered
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pending Deliveries */}
      <div className="deliveries-list">
        <h2>📦 Your Deliveries ({deliveries.length})</h2>
        {deliveries.length === 0 ? (
          <div className="no-deliveries">
            <p>🎉 No pending deliveries</p>
            <p>Check back soon or contact dispatch</p>
          </div>
        ) : (
          deliveries.map(delivery => (
            <div key={delivery.id} className="delivery-card">
              <div className="delivery-header">
                <span className="order-num">{delivery.orderNumber}</span>
                <span className={`status-badge ${delivery.status}`}>{delivery.status}</span>
              </div>
              <div className="delivery-info">
                <p><strong>Customer:</strong> {delivery.customer}</p>
                <p><strong>Address:</strong> {delivery.address}</p>
                <p><strong>Window:</strong> {delivery.deliveryTime}</p>
                <p><strong>Chef:</strong> {delivery.chef}</p>
              </div>
              <div className="delivery-items">
                <strong>Items:</strong>
                {delivery.items.map((item, idx) => (
                  <div key={idx}>{item.quantity}x {item.name}</div>
                ))}
                <div className="item-total">Total: {formatPrice(delivery.total)}</div>
              </div>
              <div className="delivery-actions">
                {delivery.status === 'ready' && (
                  <button 
                    className="btn-start"
                    onClick={() => handleStartDelivery(delivery)}
                  >
                    🚗 Start Delivery
                  </button>
                )}
                <a href={`tel:${delivery.phone}`} className="btn-call-small">📞</a>
                <a href={`tel:${delivery.chefPhone}`} className="btn-call-small">👨‍🍳</a>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Debug Panel */}
      {process.env.NODE_ENV === 'development' && currentLocation && (
        <div className="debug-panel">
          <h4>📍 Debug Info</h4>
          <p>Lat: {currentLocation.lat}</p>
          <p>Lng: {currentLocation.lng}</p>
          <p>Accuracy: {currentLocation.accuracy}m</p>
          <p>Last Update: {new Date(currentLocation.timestamp).toLocaleTimeString()}</p>
        </div>
      )}
    </div>
  );
}

